/*
 * Simax custom: places a Yotpo on-site widget in a Testimonials section.
 *
 * <simax-yotpo-reviews> is rendered by snippets/simax-yotpo-reviews.liquid with
 * the Yotpo widget types it accepts, in order of preference, and (on product
 * pages) the product's data-yotpo-* attributes. The instance ID is looked up by
 * widget type in the config of the Yotpo loader that the Yotpo Product Reviews
 * app embed puts on every page. Each store (dev / production) therefore uses the
 * widgets published in its own Yotpo account, with no per-store setting.
 *
 * The widget div is the same markup Yotpo's own app block outputs. Yotpo renders
 * it. If the page already has that widget for the same product (the default
 * product template has Yotpo's block right above Testimonials), the section is
 * hidden so the reviews don't show twice.
 *
 * If Yotpo isn't on the page or no matching widget is published, the section is
 * hidden on the storefront rather than falling back to the hand-typed reviews.
 * The theme editor shows the reason instead.
 */
if (!customElements.get('simax-yotpo-reviews')) {
  const LOADER_WAIT_MS = 8000;
  const POLL_MS = 200;

  class SimaxYotpoReviews extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;

      this.mount().catch((error) => this.fail(error));
    }

    async mount() {
      const widgets = await this.waitForYotpoWidgets();
      const types = this.dataset.widgetTypes.split(',');
      const instanceId = this.findInstance(widgets, types);
      if (!instanceId) throw new Error(`No ${types.join(' or ')} widget is published in Yotpo.`);

      if (this.isShownElsewhere(instanceId)) {
        this.hideSection();
        return;
      }

      const widget = document.createElement('div');
      widget.className = 'yotpo-widget-instance';
      widget.dataset.yotpoInstanceId = instanceId;
      Object.entries(this.dataset).forEach(([key, value]) => {
        if (key.startsWith('yotpo')) widget.dataset[key] = value;
      });
      this.appendChild(widget);

      // Yotpo scans the page once its initializer loads. If that already
      // happened, ask it to pick up the new widget (it skips rendered ones).
      if (typeof window.yotpoWidgetsContainer.initWidgets === 'function') {
        window.yotpoWidgetsContainer.initWidgets();
      }
    }

    // Resolves to { instanceId: widgetType } from every Yotpo account on the page.
    waitForYotpoWidgets() {
      const startedAt = Date.now();

      return new Promise((resolve, reject) => {
        const check = () => {
          const guids = (window.yotpoWidgetsContainer && window.yotpoWidgetsContainer.guids) || {};
          const widgets = {};
          Object.values(guids).forEach((guid) => {
            const configured = (guid && guid.config && guid.config.widgets) || {};
            Object.entries(configured).forEach(([id, widget]) => {
              widgets[id] = widget.className;
            });
          });

          if (Object.keys(widgets).length > 0) return resolve(widgets);
          if (Date.now() - startedAt > LOADER_WAIT_MS) {
            return reject(new Error('Yotpo did not load. Is the Yotpo Product Reviews app embed on?'));
          }
          setTimeout(check, POLL_MS);
        };

        check();
      });
    }

    findInstance(widgets, types) {
      for (const type of types) {
        const id = Object.keys(widgets).find((instanceId) => widgets[instanceId] === type);
        if (id) return id;
      }
      return null;
    }

    isShownElsewhere(instanceId) {
      return Array.from(document.querySelectorAll(`.yotpo-widget-instance[data-yotpo-instance-id="${instanceId}"]`)).some(
        (widget) => !this.contains(widget) && widget.dataset.yotpoProductId === this.dataset.yotpoProductId
      );
    }

    hideSection() {
      const section = this.closest('.shopify-section');
      if (section) section.style.display = 'none';
    }

    fail(error) {
      if (window.Shopify && window.Shopify.designMode) {
        this.textContent = `Yotpo reviews could not be shown: ${error.message} This section is hidden on the live store until they can.`;
        return;
      }

      this.hideSection();
    }
  }

  customElements.define('simax-yotpo-reviews', SimaxYotpoReviews);
}
